/** Keys whose value is a date or a date-time (order dates, survey, loan payment dates, party birth). */
export const DATE_KEY_RE = /(_date|\.birth)$/;

const pad = (value: number): string => String(value).padStart(2, '0');

/** `2024-09-06T14:23:05-06:00`: the local wall-clock time with the local UTC offset. */
export const formatLocalIso = (date: Date): string => {
  const offset = -date.getTimezoneOffset();
  const sign = offset < 0 ? '-' : '+';
  const hours = pad(Math.floor(Math.abs(offset) / 60));
  const minutes = pad(Math.abs(offset) % 60);
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${hours}:${minutes}`;
};

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
/** Midnight UTC is how a date without a time is usually stored; it is a placeholder, not a time. */
const MIDNIGHT_UTC_RE = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(\.0+)?Z$/;
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

/**
 * The calendar day at the current local time of day, with the offset that applies on that
 * day (so a January date keeps winter time even when extracted in summer). Null when the
 * day does not exist.
 */
const dayAtCurrentTime = (year: number, month: number, day: number, now: Date): string | null => {
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  const candidate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
  const exists = candidate.getFullYear() === year && candidate.getMonth() === month - 1 && candidate.getDate() === day;
  return exists ? formatLocalIso(candidate) : null;
};

/**
 * Normalises a date value for the order. A date with no known time gets the current local
 * time (never midnight). A date-time is shown in local time. Anything else, including an
 * impossible calendar date, is returned unchanged.
 */
export const normalizeDateValue = (value: string, now: Date = new Date()): string => {
  const text = value.trim();
  const dateOnly = DATE_ONLY_RE.exec(text);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return dayAtCurrentTime(Number(year), Number(month), Number(day), now) ?? text;
  }
  const us = US_DATE_RE.exec(text);
  if (us) {
    const [, month, day, year] = us;
    return dayAtCurrentTime(Number(year), Number(month), Number(day), now) ?? text;
  }
  const midnight = MIDNIGHT_UTC_RE.exec(text);
  if (midnight) {
    const [, year, month, day] = midnight;
    return dayAtCurrentTime(Number(year), Number(month), Number(day), now) ?? text;
  }
  const parsed = new Date(text);
  if (/^\d{4}-\d{2}-\d{2}T/.test(text) && !Number.isNaN(parsed.getTime())) return formatLocalIso(parsed);
  return text;
};
